import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import { ChevronRight, Copy, FileJson } from 'lucide-react';
import { FieldPeekPanel } from '../field-peek-panel';
import { FieldValue } from '../field-value';
import { useDocumentInspector } from './use-document-inspector';

export function DocumentInspector(props: Parameters<typeof useDocumentInspector>[0]) {
  const f = useDocumentInspector(props);

  if (!f.document) return null;

  return (
    <>
      <Sheet open={f.open} onOpenChange={f.onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Document Inspector</SheetTitle>
            <SheetDescription>{f.document.path}</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Document ID</h3>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-2 py-1 bg-muted rounded text-sm font-mono">
                  {f.document.id}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={f.handleCopyId}
                  title="Copy Document ID"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {f.copied && <p className="text-xs text-muted-foreground">Copied!</p>}
            </div>

            {f.showTimestamps && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Timestamps</h3>
                <div className="space-y-1 text-sm">
                  {f.document.createTime && (
                    <div>
                      <span className="text-muted-foreground">Created: </span>
                      <span>{f.formatFieldValue(f.document.createTime)}</span>
                    </div>
                  )}
                  {f.document.updateTime && (
                    <div>
                      <span className="text-muted-foreground">Updated: </span>
                      <span>{f.formatFieldValue(f.document.updateTime)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Fields</h3>
              <div className="space-y-3">
                {f.fieldEntries.map(({ key, value, isClickable }) => (
                  <div key={key} className="border-b pb-3 last:border-0">
                    <div className="text-xs font-mono text-muted-foreground mb-1">{key}</div>
                    <FieldValue
                      value={value}
                      maxLength={100}
                      onPeek={isClickable ? (val, type) => f.handlePeek(val, type, key) : undefined}
                    />
                  </div>
                ))}
              </div>
            </div>

            {f.subcollections.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Subcollections</h3>
                <div className="space-y-1">
                  {f.subcollections.map((sub) => (
                    <button
                      key={sub.name}
                      onClick={() => f.handleSubcollectionClick(sub.name)}
                      className="w-full cursor-pointer text-left px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors flex items-center gap-2"
                    >
                      <ChevronRight className="h-4 w-4" />
                      <span>{sub.name}</span>
                      {sub.documentCount !== undefined && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {f.getSubcollectionDocLabel(sub.documentCount)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2 pt-4 border-t">
              <Button onClick={f.onViewJson} variant="default" className="w-full">
                <FileJson className="h-4 w-4 mr-2" />
                View JSON
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {f.peekValue && (
        <FieldPeekPanel
          open={!!f.peekValue}
          onOpenChange={f.handlePeekClose}
          fieldName={f.peekValue.fieldName}
          value={f.peekValue.value}
          fieldType={f.peekValue.fieldType}
        />
      )}
    </>
  );
}
